import jStat from 'jstat';
import type { RegressionParams, PolynomialRegressionParams, RegressionResult, GeneratePointsParams } from './types';

function generateRegressionPoints({ x, fn, numPoints = 100 }: GeneratePointsParams): [number, number][] {
  const min = Math.min(...x);
  const max = Math.max(...x);
  const step = (max - min) / (numPoints - 1);

  return Array.from({ length: numPoints }, (_, i) => {
    const x = min + i * step;
    return [x, fn(x)];
  });
}

/**
 * Calculates linear regression
 * y = mx + b
 */
export function linearRegression({ x, y }: RegressionParams): RegressionResult {
  if (x.length === 0 || y.length === 0 || x.length !== y.length) {
    throw new Error('Invalid input arrays');
  }

  // Calculate slope and intercept using jStat's built-in correlation
  const n = x.length;
  const xMean = jStat.mean(x);
  const yMean = jStat.mean(y);
  const r = jStat.corrcoeff(x, y);
  const xStd = jStat.stdev(x, true);
  const yStd = jStat.stdev(y, true);
  
  const slope = r * (yStd / xStd);
  const intercept = yMean - slope * xMean;

  // R² is just the square of correlation coefficient
  const r2 = r * r;

  // Generate points for the regression line
  const points = generateRegressionPoints({ 
    x, 
    fn: (x) => slope * x + intercept 
  });

  return {
    points,
    equation: `y = ${slope.toFixed(3)}x + ${intercept.toFixed(3)}`,
    r2,
    r,
  };
}

/**
 * Calculates polynomial regression (quadratic)
 * y = ax² + bx + c
 */
export function polynomialRegression({ x, y, degree = 2 }: PolynomialRegressionParams): RegressionResult {
  if (x.length === 0 || y.length === 0 || x.length !== y.length) {
    throw new Error('Invalid input arrays');
  }

  const n = x.length;

  // Create design matrix X
  const X: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row: number[] = [];
    for (let j = degree; j >= 0; j--) {
      const xi = x[i];
      if (xi === undefined) continue;
      row.push(Math.pow(xi, j));
    }
    X.push(row);
  }

  // Create y matrix
  const Y = Array.from(y, yi => [yi]);

  // Calculate coefficients using normal equation: β = (X'X)^(-1)X'y
  const XT = jStat.transpose(X);
  const XTX = jStat.multiply(XT, X);
  const XTXInv = jStat.inv(XTX);
  const XTY = jStat.multiply(XT, Y);
  const coefficients = jStat.multiply(XTXInv, XTY).map((row: number[]) => row[0] ?? 0);

  // Calculate R²
  const yMean = jStat.sum(y) / n;
  const yPred = x.map(xi => {
    if (xi === undefined) return 0;
    let sum = 0;
    for (let j = 0; j <= degree; j++) {
      const coef = coefficients[j] ?? 0;
      sum += coef * Math.pow(xi, degree - j);
    }
    return sum;
  });

  const residuals = Array.from(y.entries(), ([i, yi]) => {
    if (yi === undefined) return 0;
    const pred = yPred[i] ?? 0;
    return Math.pow(yi - pred, 2);
  });
  const ssRes = jStat.sum(residuals);
  const deviations = y.map(yi => yi !== undefined ? Math.pow(yi - yMean, 2) : 0);
  const ssTot = jStat.sum(deviations);
  const r2 = 1 - (ssRes / ssTot);
  const r = Math.sqrt(r2);

  // Generate points for the polynomial curve
  const points = generateRegressionPoints({ 
    x, 
    fn: (x) => {
      let sum = 0;
      for (let j = 0; j <= degree; j++) {
        const coef = coefficients[j] ?? 0;
        sum += coef * Math.pow(x, degree - j);
      }
      return sum;
    }
  });

  // Create equation string
  const terms = coefficients.filter((coef: unknown): coef is number => coef !== undefined)
    .map((coef: number, i: number) => {
      const power = degree - i;
      if (power === 0) return coef.toFixed(3);
      if (power === 1) return `${coef.toFixed(3)}x`;
      return `${coef.toFixed(3)}x^${power}`;
    });
  const equation = `y = ${terms.join(' + ')}`;

  return {
    points,
    equation,
    r2,
    r,
  };
}

/**
 * Calculates exponential regression
 * y = ae^(bx)
 */
export function exponentialRegression({ x, y }: RegressionParams): RegressionResult {
  if (x.length === 0 || y.length === 0 || x.length !== y.length) {
    throw new Error('Invalid input arrays');
  }

  // Check for non-positive y values which would cause issues with log
  if (y.some(val => val <= 0)) {
    throw new Error('Exponential regression requires all y values to be positive');
  }

  // Take natural log of y values to convert to linear form
  const lnY = y.map(yi => Math.log(yi));
  
  // Use linear regression on transformed data
  const n = x.length;
  const sumX = jStat.sum(x);
  const sumLnY = jStat.sum(lnY);
  const xlnY = Array.from(x.entries(), ([i, xi]) => xi * lnY[i]!);
  const sumXLnY = jStat.sum(xlnY);
  const xx = x.map(xi => xi * xi);
  const sumXX = jStat.sum(xx);
  
  const b = (n * sumXLnY - sumX * sumLnY) / (n * sumXX - sumX * sumX);
  const lnA = (sumLnY - b * sumX) / n;
  const a = Math.exp(lnA);
  
  // Calculate correlation coefficient on transformed data
  const xMean = sumX / n;
  const lnYMean = sumLnY / n;
  const numerator = sumXLnY - n * xMean * lnYMean;
  const lnYY = lnY.map(yi => yi * yi);
  const denominator = Math.sqrt((sumXX - n * xMean * xMean) * (jStat.sum(lnYY) - n * lnYMean * lnYMean));
  const r = numerator / denominator;
  const r2 = r * r;

  // Generate points for the exponential curve
  const points = generateRegressionPoints({ 
    x, 
    fn: (x) => a * Math.exp(b * x) 
  });

  return {
    points,
    equation: `y = ${a.toFixed(3)}e^(${b.toFixed(3)}x)`,
    r2,
    r,
  };
} 